import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $initialAccountsStore, $initialCardsStore, $usersStore } from '@lib/store';
import { useTabs } from '@shared/hooks';
import { cn, ValidateUtils } from '@shared/utils';
import { UserType, type CardResponse, type UsersRequest } from '@shared/api';
import { AccountsAndCardsPicker } from '@widgets';
import { BaseInput, Button, Modal, PhoneInput, Radio } from '@shared/widgets';
import { Constants } from '@shared/constants';

export interface AddNewUserFormData {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
  userType: UserType;
  accounts: number[];
  cards: CardResponse[];
}
const DEFAULT_VALUES: AddNewUserFormData = {
  userName: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  position: '',
  phone: { countryCode: Constants.COUNTRY_CODE, phoneNumber: '' },
  userType: UserType.User,
  accounts: [],
  cards: [],
};

interface AddNewUserModalProps {
  show: boolean;
  onClose: () => void;
  params: UsersRequest;
}

export const AddNewUserModal = (props: AddNewUserModalProps) => {
  const accountsStore = useUnit($initialAccountsStore.store);
  const cardsStore = useUnit($initialCardsStore.store);

  const tabState = useTabs();
  const form = useForm<AddNewUserFormData>({ defaultValues: DEFAULT_VALUES });
  const { control, formState, handleSubmit, setValue, watch, reset } = form;
  const userType = watch('userType');

  const { t } = useTranslation('userManagement');

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: AddNewUserFormData) => {
    await $usersStore.events.addUser({
      user: {
        userName: data.userName,
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.emailAddress,
        position: data.position,
        phoneAreaCode: data.phone.countryCode,
        phoneNumber: data.phone.phoneNumber,
        userType: data.userType,
        accountId: data.accounts,
        cardId: data.cards.map((card) => card.cardId),
      },
      params: props.params,
    });
    closeHandler();
  };

  const userTypeOptions = useMemo(
    () => [
      { value: UserType.Manager, text: t('modals.add.userTypes.manager') },
      { value: UserType.User, text: t('modals.add.userTypes.user') },
    ],
    [t],
  );
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('modals.add.title')}>
      <FormProvider {...form}>
        <form className={cn('flex w-[640px] flex-col gap-4', 'max-md:w-[308px] max-md:gap-2')} noValidate onSubmit={handleSubmit(submitHandler)}>
          <div className={cn('flex gap-4', 'max-md:flex-col max-md:gap-2')}>
            <Controller
              name='userName'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                minLength: { value: 6, message: t('modals.add.minimumError') },
                maxLength: { value: 30, message: t('errors.form.maxCharacters', { ns: 'common', max: 30 }) },
                pattern: { value: ValidateUtils.noSpacesRegex, message: t('errors.form.noSpaces', { ns: 'common' }) },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('placeholders.username', { ns: 'common' })}
                  placeholder={t('placeholders.username', { ns: 'common' })}
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='firstName'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('placeholders.firstName', { ns: 'common' })}
                  placeholder={t('placeholders.firstName', { ns: 'common' })}
                  required
                  error={error?.message}
                />
              )}
            />
          </div>
          <div className={cn('flex gap-4', 'max-md:flex-col max-md:gap-2')}>
            <Controller
              name='lastName'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('placeholders.lastName', { ns: 'common' })}
                  placeholder={t('placeholders.lastName', { ns: 'common' })}
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='emailAddress'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('placeholders.email', { ns: 'common' })}
                  type='email'
                  placeholder={t('placeholders.email', { ns: 'common' })}
                  required
                  error={error?.message}
                />
              )}
            />
          </div>
          <div className={cn('flex gap-4', 'max-md:flex-col max-md:gap-2')}>
            <Controller
              name='position'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                maxLength: { value: 64, message: t('errors.form.maxCharacters', { ns: 'common', max: 64 }) },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('placeholders.positionRole', { ns: 'common' })}
                  placeholder={t('placeholders.positionRole', { ns: 'common' })}
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='phone'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                validate: (value) => {
                  if (!value?.countryCode) return t('errors.form.required', { ns: 'common' });
                  if (!value?.phoneNumber) return t('errors.form.required', { ns: 'common' });
                  if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return t('errors.form.phone.invalidFormat', { ns: 'common' });
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <PhoneInput label='Phone' enableSearch required value={field.value} onChange={field.onChange} error={error?.message} />
              )}
            />
          </div>
          <Controller
            name='userType'
            control={control}
            render={({ field }) => (
              <Radio
                orientation='horizontal'
                options={userTypeOptions}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('accounts', []);
                  setValue('cards', []);
                }}
              />
            )}
          />
          {userType === UserType.Manager && <AccountsAndCardsPicker title='Add Accounts' accounts={accountsStore.data} noCards />}
          {userType === UserType.User && (
            <AccountsAndCardsPicker
              title={tabState.hasPayment ? 'Add Payment Accounts and Cards' : 'Add Cards'}
              hint={
                tabState.hasPayment
                  ? 'Use the checkbox on a Payment Account to link all of its cards. Expand an account to select individual cards.'
                  : undefined
              }
              accounts={accountsStore.data}
              cards={cardsStore.data}
              onlyPaymentAccounts
            />
          )}
          <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('buttons.saveChanges', { ns: 'common' })}
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
};
